// ============================================================
// TERROR ENGINE — ROOM OVERLAY  (the persistent overseer)
// ------------------------------------------------------------
// One overarching system that renders the always-present
// bottom-left control stack in EVERY Terror room except Pierre:
//
//        ┌───────────┐
//        │ 💬  (chat) │   ← sits JUST ABOVE menu
//        └───────────┘
//        ┌───────────┐
//        │  ☰ MENU   │   ← the front door
//        └───────────┘
//
// WHY THIS EXISTS
//   Before this, the MENU button and the chat bubble were both
//   hardcoded to the bottom-left corner and OVERLAPPED. Worse,
//   chat only existed inside Plaza — walk into any other room and
//   it vanished. This component is the single source of truth for
//   that control stack so the layout + behaviour stay identical
//   everywhere. One edit propagates to every room.
//
// CHANNEL MODEL
//   Chat runs on ONE shared channel — zoneId "lobby" — so the same
//   people stay together no matter which room they walk into. Plaza
//   keeps its own in-world floaties (it's the only room that tracks
//   player positions + camera), so Plaza does NOT use this overlay's
//   chat; it keeps its existing <ChatSystem> on the "plaza" channel.
//   Every other room gets the lobby panel + toggle via this overlay.
//
// USAGE  (wrap a room's return in App.js)
//   import RoomOverlay from "./RoomOverlay";
//
//   if (screen === "workshop") return (
//     <RoomOverlay profile={workshopProfile} onExitToPlaza={() => setScreen("plaza")}>
//       <WilliesWorkshop ... />
//     </RoomOverlay>
//   );
//
// PROPS
//   profile        { uid, name, ... } — the room's own profile object.
//                                       uid/name are read off this.
//   zoneId         string ("lobby")   — chat channel. Defaults "lobby".
//   chat           bool   (true)      — set false to suppress the chat
//                                       toggle (e.g. a room that owns
//                                       its own in-world chat).
//   menu           bool   (true)      — set false to suppress MENU.
//   sections       array  (optional)  — extra StartMenu sections MERGED
//                                       with the base set. A room can
//                                       inject its own interactables.
//   onExitToPlaza  fn     (optional)  — wired into the base menu's
//                                       "Return to Plaza" leaf.
//   children       the room itself.
//
// The overlay is presentation-only over navigation: every leaf just
// fires a callback. It never reaches into a room's internals.
// ============================================================

import { useState } from "react";
import { GM } from "./theme";
import StartMenu from "./StartMenu";
import { ChatSystem, useChatSystem } from "./ChatSystem";
import { BackdropAtelier, BackdropUnderlay, StageHairline } from "./RoomBackdrop";
import { soundMenuSection } from "./Soundtrack";
import { useRail, useRailHost, RailFoldButton, RailMenuPill } from "./railDock";

// ── Shared geometry. Desktop: MENU + chat live in the right-column
//    CHROME RAIL (matches Plaza, caP'n July 8). Mobile: the old
//    bottom-left stack (thumb reach). ────────────────────────────
const STACK_LEFT      = 16;   // px from left edge — MENU button (mobile)
const MENU_BOTTOM_MOB = 26;   // px from bottom — mobile (above dock)
const MENU_H_EST      = 38;   // approx MENU button height
const STACK_GAP       = 12;   // gap between MENU and chat bubble (mobile)
const RAIL_RIGHT      = 12;   // px from right — desktop rail
const RAIL_TOP        = 12;   // MENU top in the rail
const RAIL_CHAT_TOP   = 60;   // chat bubble top in the rail (under MENU)
const RAIL_W          = "min(280px, 24vw)";  // same band as the console

function isMobile() {
  if (typeof window === "undefined") return false;
  return window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
}

// ─────────────────────────────────────────────────────────────
// Base menu sections — the universal front door that makes sense
// in ANY room. Rooms can inject more via the `sections` prop.
// ─────────────────────────────────────────────────────────────
// ⭐ ONE ROOM CATEGORY, NOT TWO CATEGORIES OF ONE ROW.
// This built "Navigate → Return to Plaza" and "Customize → Backdrop
// Atelier": two drills, two keypresses, one option each, in EVERY
// wrapped room in the game. The Plaza's front-door rebuild on
// 2026-08-20 called that defect out by name and fixed it there — and
// then left the same shape standing in the overseer that wraps eleven
// other screens, which is the more common half of it.
//
// Both rows are things you do TO the room you are standing in, so they
// are one category and it is called ROOM. Sound stays its own section
// for the reason documented at the Plaza's menu: it is a shared
// contract with another screen and inlining it here would fork it.
function buildBaseSections({ onExitToPlaza, onOpenAtelier }) {
  const sections = [];
  const roomItems = [];
  if (onExitToPlaza) roomItems.push({ id: "to_plaza",  label: "Return to Plaza",  run: () => onExitToPlaza() });
  // The Backdrop Atelier lives in the OVERSEER so every wrapped room
  // gets it with zero per-room wiring (one edit, all rooms).
  if (onOpenAtelier) roomItems.push({ id: "backdrops", label: "Backdrop Atelier", run: () => onOpenAtelier() });
  if (roomItems.length) sections.push({ id: "room", label: "Room", icon: "⌖", items: roomItems });
  // ♪ Sound, in every wrapped room, from the one shared builder.
  sections.push(soundMenuSection());
  return sections;
}

// ─────────────────────────────────────────────────────────────
// MenuButton — the ☰ MENU front-door button. GM chrome palette.
// Desktop: full-width pill at the top of the right rail. Mobile:
// bottom-left.
// ─────────────────────────────────────────────────────────────
function MenuButton({ onClick, mobile }) {
  return (
    <button
      onClick={onClick}
      aria-label="Open menu"
      style={{
        position: "fixed",
        ...(mobile
          ? { left: STACK_LEFT, bottom: MENU_BOTTOM_MOB }
          : { right: RAIL_RIGHT, top: RAIL_TOP, width: RAIL_W, justifyContent: "center" }),
        zIndex: 70,
        display: "flex", alignItems: "center", gap: 6,
        padding: mobile ? "10px 16px" : "8px 16px",
        background: `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageWarm})`,
        border: `1px solid ${GM.inkGold}`,
        borderRadius: 22,
        color: GM.vellum, fontFamily: GM.font,
        fontSize: 12, fontWeight: 700, letterSpacing: 2,
        cursor: "pointer",
        boxShadow: `0 0 14px ${GM.inkGold}44`,
        userSelect: "none", WebkitUserSelect: "none",
      }}
    >
      ☰ MENU
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// RoomOverlay — wrap a room, get the persistent control stack.
// ─────────────────────────────────────────────────────────────
export default function RoomOverlay({
  profile,
  zoneId = "lobby",
  chat = true,
  menu = true,
  sections = [],
  onExitToPlaza,
  children,
}) {
  const uid        = profile?.uid || null;
  const playerName = profile?.name || "Aenarchist";
  const mobile     = isMobile();

  const [showMenu, setShowMenu]       = useState(false);
  const [showAtelier, setShowAtelier] = useState(false);

  // Lobby chat — no `pos` outside Plaza, so floaties never render here;
  // the panel + toggle work fine on the shared channel regardless.
  const chatState = useChatSystem({ zoneId, uid, playerName, pos: null });

  // THE RAIL (2026-09-23) — this overseer hosts the backdrop band for every
  // wrapped room: Record / chat / Friends take turns, and the whole band
  // folds into a draggable MENU pill. Desktop only; see railDock.js.
  useRailHost(!!uid && !mobile);
  const r = useRail();
  const folded = r.active && r.collapsed;

  const menuSections = [
    ...buildBaseSections({
      onExitToPlaza,
      onOpenAtelier: () => { setShowMenu(false); setShowAtelier(true); },
    }),
    ...sections,
  ];

  // Chat bubble placement. Desktop: docked into the right rail, just
  // under MENU (or in MENU's own slot if MENU is suppressed). Mobile:
  // the old bottom-left stack, lifted to clear the MENU pill.
  let togglePos;
  if (mobile) {
    const baseBottom = MENU_BOTTOM_MOB;
    const chatBottom = menu ? baseBottom + MENU_H_EST + STACK_GAP : baseBottom;
    togglePos = { bottom: chatBottom, left: STACK_LEFT + 2 };
  } else {
    togglePos = { right: RAIL_RIGHT, top: menu ? RAIL_CHAT_TOP : RAIL_TOP };
  }

  return (
    <>
      {/* BACKDROP UNDERLAY — the equipped backdrop UNDER every wrapped
          room (rooms have transparent roots, so it shows through all
          letterbox space). Lives below the room, so a room's own fixed
          HUD/console z-indexes always win — the Faction Hall Record
          bug can't recur. Room-local <RoomBackdrop> layers are now
          redundant but harmless — cleanup in the room pass. */}
      <BackdropUnderlay uid={uid} />

      {children}

      {/* STAGE HAIRLINE — 1px gold outline of the canonical rectangle,
          painted over everything (pointer-events none) so rectangle
          consistency stays verifiable by eye during the room pass. */}
      <StageHairline />

      {/* MENU — persistent front door */}
      {menu && uid && !showMenu && !folded && (
        <MenuButton onClick={() => setShowMenu(true)} mobile={mobile} />
      )}
      {uid && !showMenu && <RailFoldButton />}
      {uid && !showMenu && <RailMenuPill />}

      {menu && showMenu && (
        <StartMenu sections={menuSections} onClose={() => setShowMenu(false)} />
      )}

      {/* BACKDROP ATELIER — browse/buy/equip letterbox backdrops */}
      {showAtelier && (
        <BackdropAtelier profile={profile} onClose={() => setShowAtelier(false)} />
      )}

      {/* CHAT — shared lobby channel, bubble stacked above MENU via
          ChatSystem's prop-driven togglePos. The open panel is a
          full-width slide-up and is unaffected by togglePos. */}
      {chat && uid && (
        <ChatSystem {...chatState} togglePos={togglePos} railed />
      )}
    </>
  );
}
